import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { PageHeader, Row, Section, Select } from '../components/form';
import { Alert, Badge, Button, Input } from '../components/ui';
import { ApiError, api, type Me } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'viewer';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

const ROLES = [
  { value: 'owner', label: 'Owner', hint: 'Full control, including managing other users.' },
  { value: 'admin', label: 'Administrator', hint: 'Everything except user management.' },
  { value: 'viewer', label: 'Viewer', hint: 'Read only.' },
] as const;

const ROLE_TONE: Record<string, 'success' | 'muted' | 'warning'> = {
  owner: 'success',
  admin: 'muted',
  viewer: 'warning',
};

/** Who can administer this PBX. */
export function Users({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<User[]>('/api/v1/users'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<User> }) =>
      api.patch(`/api/v1/users/${id}`, patch),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'could not update'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/users/${id}`),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'could not delete'),
  });

  const isOwner = me.role === 'owner';

  const columns: Array<Column<User>> = [
    {
      key: 'email',
      header: 'Email',
      sort: (a, b) => a.email.localeCompare(b.email),
      render: (u) => (
        <span>
          <span className="font-medium">{u.email}</span>
          {u.id === me.id && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (u) => u.name,
    },
    {
      key: 'role',
      header: 'Role',
      sort: (a, b) => a.role.localeCompare(b.role),
      render: (u) =>
        isOwner ? (
          <div className="w-36">
            <Select
              value={u.role}
              onChange={(role) =>
                update.mutate({ id: u.id, patch: { role: role as User['role'] } })
              }
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <Badge tone={ROLE_TONE[u.role] ?? 'muted'}>{u.role}</Badge>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) =>
        u.isActive ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="destructive">Disabled</Badge>
        ),
    },
    {
      key: 'lastLogin',
      header: 'Last signed in',
      sort: (a, b) => (a.lastLoginAt ?? '').localeCompare(b.lastLoginAt ?? ''),
      render: (u) =>
        u.lastLoginAt ? (
          new Date(u.lastLoginAt).toLocaleString()
        ) : (
          <span className="text-muted-foreground">Never</span>
        ),
    },
    {
      key: 'actions',
      header: 'Access',
      align: 'right',
      render: (u) =>
        isOwner ? (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="h-8 px-2 text-xs" onClick={() => setResetting(u)}>
              <KeyRound size={13} />
              Password
            </Button>
            <Button
              variant="ghost"
              className="h-8 px-2 text-xs"
              onClick={() => update.mutate({ id: u.id, patch: { isActive: !u.isActive } })}
            >
              {u.isActive ? 'Disable' : 'Enable'}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Accounts that can sign in to this admin. Separate from extensions."
        actions={
          isOwner && (
            <Button onClick={() => setCreating(true)}>
              <Plus size={14} />
              New user
            </Button>
          )
        }
      />

      {error && <Alert>{error}</Alert>}

      <TwoFactor me={me} />

      {!isOwner && (
        <Alert tone="warning">
          Only owners can add users or change roles. You are signed in as {me.role}.
        </Alert>
      )}

      {creating && (
        <CreateForm
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            invalidate();
          }}
        />
      )}

      {resetting && (
        <ResetForm
          user={resetting}
          onDone={() => {
            setResetting(null);
            invalidate();
          }}
        />
      )}

      <DataTable
        rows={users}
        columns={columns}
        rowKey={(u) => u.id}
        search={(u) => `${u.email} ${u.name} ${u.role}`}
        searchPlaceholder="Search users"
        {...(isOwner
          ? {
              onDelete: (rows: readonly User[]) => rows.forEach((r) => remove.mutate(r.id)),
              deleteLabel: (rows: readonly User[]) =>
                `Delete ${rows.length} user(s)? They are signed out immediately and cannot sign back in.`,
            }
          : {})}
        empty="No users."
      />
    </div>
  );
}

function CreateForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('admin');

  const create = useMutation({
    mutationFn: () => api.post('/api/v1/users', { email, name, password, role }),
    onSuccess: onCreated,
  });

  const conflict = create.error instanceof ApiError && create.error.isConflict;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Section title="New user">
        <div className="grid gap-4 sm:grid-cols-2">
          <Row
            label="Email"
            required
            error={conflict ? 'That email address is already in use' : undefined}
          >
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Row>

          <Row label="Name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Row>

          <Row
            label="Password"
            required
            hint="At least 12 characters. A passphrase beats a short complicated string."
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              required
            />
          </Row>

          <Row label="Role" required hint={ROLES.find((r) => r.value === role)?.hint}>
            <Select value={role} onChange={setRole}>
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </Row>
        </div>

        {create.error && !conflict && (
          <div className="mt-4">
            <Alert>{(create.error as Error).message}</Alert>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create user'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Section>
    </form>
  );
}

function ResetForm({ user, onDone }: { user: User; onDone: () => void }) {
  const [password, setPassword] = useState('');

  const reset = useMutation({
    mutationFn: () => api.post(`/api/v1/users/${user.id}/password`, { password }),
    onSuccess: onDone,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        reset.mutate();
      }}
    >
      <Section
        title={`Set a new password for ${user.email}`}
        description="They are signed out everywhere, which is the point of a reset."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Row label="New password" required hint="At least 12 characters.">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              required
            />
          </Row>
        </div>

        {reset.error && (
          <div className="mt-4">
            <Alert>{(reset.error as Error).message}</Alert>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={reset.isPending || password.length < 12}>
            {reset.isPending ? 'Saving…' : 'Set password'}
          </Button>
          <Button type="button" variant="secondary" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </Section>
    </form>
  );
}

/**
 * Two-factor for the signed-in account.
 *
 * Deliberately about YOUR account only — an owner cannot enrol somebody else, because enrolment
 * means holding their secret, and a second factor an administrator can set up for you is not a
 * second factor.
 */
function TwoFactor({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [disabling, setDisabling] = useState(false);

  const begin = useMutation({
    mutationFn: () => api.post<{ secret: string; uri: string }>('/api/v1/auth/totp/begin'),
    onSuccess: setSetup,
  });

  const confirm = useMutation({
    mutationFn: () => api.post('/api/v1/auth/totp/confirm', { secret: setup?.secret, code }),
    onSuccess: () => {
      // Enrolling revoked every session including this one, so there is nothing to refresh —
      // a reload lands on the sign-in page, which is the honest outcome rather than a UI that
      // pretends to still be signed in until the next request fails.
      window.location.reload();
    },
  });

  const disable = useMutation({
    mutationFn: () => api.post('/api/v1/auth/totp/disable', { code }),
    onSuccess: () => {
      setDisabling(false);
      setCode('');
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const error = begin.error ?? confirm.error ?? disable.error;

  if (me.totpEnabled && !disabling) {
    return (
      <Section
        title="Two-factor authentication"
        description="Your account asks for a code from your authenticator app at sign-in."
      >
        <div className="flex items-center gap-3">
          <Badge tone="success">Enabled</Badge>
          <Button variant="ghost" className="text-destructive" onClick={() => setDisabling(true)}>
            Turn off
          </Button>
        </div>
      </Section>
    );
  }

  if (disabling) {
    return (
      <Section title="Turn off two-factor authentication">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            {/* A current code is required even though you are already signed in: somebody who
                stole this session is exactly what the second factor exists to survive. */}
            <Row label="Current code" hint="From your authenticator app.">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                maxLength={6}
                autoFocus
              />
            </Row>
          </div>
          <Button
            variant="destructive"
            disabled={code.length < 6 || disable.isPending}
            onClick={() => disable.mutate()}
          >
            Turn off
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setDisabling(false);
              setCode('');
            }}
          >
            Cancel
          </Button>
        </div>
        {error ? <Alert className="mt-3">{(error as Error).message}</Alert> : null}
      </Section>
    );
  }

  return (
    <Section
      title="Two-factor authentication"
      description="A code from your phone, in addition to your password. This admin can reconfigure call routing, so it is worth having."
    >
      {!setup ? (
        <Button onClick={() => begin.mutate()} disabled={begin.isPending}>
          <KeyRound size={14} />
          Set up
        </Button>
      ) : (
        <div className="space-y-4">
          <p className="text-sm">
            Add this to your authenticator app, then enter the code it shows.
          </p>

          {/*
            The secret in plain text rather than a QR image. Rendering a QR would mean shipping a
            generator for one screen; every authenticator app accepts manual entry, and the URI
            below works as a link on the same device.
          */}
          <div className="rounded-[--radius] border border-border bg-muted/40 p-4">
            <p className="text-xs text-muted-foreground">Setup key</p>
            <p className="mt-1 select-all break-all font-mono text-sm">{setup.secret}</p>
            <a
              href={setup.uri}
              className="mt-3 inline-block text-xs text-muted-foreground underline-offset-4 hover:underline"
            >
              Open in an app on this device
            </a>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <Row label="Code from the app">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                />
              </Row>
            </div>
            <Button
              disabled={code.length < 6 || confirm.isPending}
              onClick={() => confirm.mutate()}
            >
              {confirm.isPending ? 'Checking…' : 'Turn on'}
            </Button>
            <Button variant="secondary" onClick={() => setSetup(null)}>
              Cancel
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Turning this on signs you out everywhere, including here — none of those sessions has
            passed the new check.
          </p>
        </div>
      )}

      {error ? <Alert>{(error as Error).message}</Alert> : null}
    </Section>
  );
}
